/**
 * Above Category — header state and hero load sequence (loaded defer from layout/theme.liquid).
 * Styles: assets/ac-header.css.
 *
 * Announcement bar: measures the header-group sections that are not the header and writes the sum to
 * --ac-announcement-height on <body>, which the viewport-height hero subtracts (100svh − announcement bar).
 * Kept fresh with a ResizeObserver.
 *
 * Transparent header: on a page whose first visible section is an opted-in hero (<html class="ac-hero-page">,
 * set by the hero's own inline script before paint), the header is transparent until the hero's bottom edge
 * passes under the header. An IntersectionObserver on the hero section, with the root inset by the header
 * height, flips html.ac-header-solid; there is no scroll listener. Hover, focus and open menus are handled in
 * CSS. Pages without such a hero never get .ac-hero-page and are solid from the first byte.
 *
 * Hero intro: the first hero carries .ac-hero-intro (same inline script; the journal article's split hero,
 * blocks/ac-article-hero, runs the intro without the transparent header). Once its image has loaded, .is-in is
 * added two frames later so the CSS transitions run (image settles, then eyebrow / heading / button). A safety
 * timer covers an image that never reports load. Reduced motion skips straight to .is-in.
 *
 * Compact header: when the header opts in (data-ac-compact, the "Compact on scroll" setting), a passive scroll
 * listener throttled to one frame adds html.ac-header-compact past 80px and removes it under 40px (hysteresis, so
 * the state does not flutter at one threshold). The CSS (ac-header.css §6) applies the compact bar only while the
 * header is solid. The header's rest height is written to --ac-header-rest on <body> whenever it is measured at
 * rest, so the sticky header section can hold that height and the hero pull-up can read it; neither moves when
 * the bar shrinks. Horizon scrolls the window below 990px and .page-wrapper above, so the offset is read from both.
 */
(function () {
  'use strict';

  var html = document.documentElement;
  var body = document.body;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Announcement bar height ---- */
  function measureAnnouncement() {
    var group = document.getElementById('header-group');
    if (!group) return;
    var total = 0;
    for (var i = 0; i < group.children.length; i++) {
      var el = group.children[i];
      if (el.classList && !el.classList.contains('header-section')) total += el.offsetHeight;
    }
    body.style.setProperty('--ac-announcement-height', Math.round(total) + 'px');
  }
  function watchAnnouncement() {
    measureAnnouncement();
    var group = document.getElementById('header-group');
    if (!group || !('ResizeObserver' in window)) return;
    var ro = new ResizeObserver(function () { measureAnnouncement(); });
    for (var i = 0; i < group.children.length; i++) ro.observe(group.children[i]);
  }

  /* ---- Transparent header: solid once the hero has scrolled under it ---- */
  function watchHero() {
    if (!html.classList.contains('ac-hero-page')) return;
    var hero = document.querySelector('#MainContent .ac-hero-section');
    var header = document.getElementById('header-component');
    if (!hero || !header) { html.classList.add('ac-header-solid'); return; }
    if (!('IntersectionObserver' in window)) {
      // Fallback: a passive scroll check on whichever container scrolls
      var check = function () {
        var r = hero.getBoundingClientRect();
        html.classList.toggle('ac-header-solid', r.bottom <= header.offsetHeight);
      };
      document.addEventListener('scroll', check, { passive: true, capture: true });
      check();
      return;
    }
    var io = null;
    function observe() {
      if (io) io.disconnect();
      var inset = Math.max(0, Math.round(header.offsetHeight));
      io = new IntersectionObserver(function (entries) {
        var e = entries[entries.length - 1];
        if (!e) return;
        html.classList.toggle('ac-header-solid', !e.isIntersecting);
      }, { root: null, rootMargin: '-' + inset + 'px 0px 0px 0px', threshold: 0 });
      io.observe(hero);
    }
    observe();
    // The header can change height (compact ↔ regular, resize); re-arm with the new inset.
    if ('ResizeObserver' in window) {
      var last = header.offsetHeight;
      new ResizeObserver(function () {
        var h = header.offsetHeight;
        if (Math.abs(h - last) >= 2) { last = h; observe(); }
      }).observe(header);
    }
    // The theme editor re-renders sections in place: if the hero section is swapped out, re-observe it.
    if (html.classList.contains('shopify-design-mode') && 'MutationObserver' in window) {
      new MutationObserver(function () {
        var fresh = document.querySelector('#MainContent .ac-hero-section');
        if (fresh && fresh !== hero) { hero = fresh; observe(); }
      }).observe(document.getElementById('MainContent') || body, { childList: true });
    }
  }

  /* ---- Hero intro ---- */
  function intro() {
    var hero = document.querySelector('.ac-hero-intro');
    if (!hero) return;
    if (reduce) { hero.classList.add('is-in'); return; }
    var img = hero.querySelector('.hero__media-grid img, .ac-lhero__bg img, .ac-bhero__bg img, .ac-ahero__media img, img.hero__media, .hero__media-grid video, .ac-lhero__video');
    var done = false;
    function go() {
      if (done) return;
      done = true;
      // Two frames so the hidden state has been painted and the transitions have something to run from.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { hero.classList.add('is-in'); });
      });
    }
    var timer = setTimeout(go, 1500);
    if (!img) { go(); return; }
    var ready = img.tagName === 'VIDEO' ? img.readyState >= 2 : (img.complete && img.naturalWidth > 0);
    if (ready) { clearTimeout(timer); go(); return; }
    img.addEventListener(img.tagName === 'VIDEO' ? 'loadeddata' : 'load', function () { clearTimeout(timer); go(); }, { once: true });
    img.addEventListener('error', function () { clearTimeout(timer); go(); }, { once: true });
  }

  /* ---- Compact header on scroll ---- */
  function watchCompact() {
    var header = document.getElementById('header-component');
    if (!header || !header.hasAttribute('data-ac-compact')) return;
    var ON = 80, OFF = 40, CLASS = 'ac-header-compact';
    var pw = document.querySelector('.page-wrapper');
    var ticking = false;
    var settle = null;

    function offset() {
      var w = window.scrollY || window.pageYOffset || 0;
      var p = pw ? pw.scrollTop : 0;
      return Math.max(w, p);
    }
    var holdUntil = 0; // no measuring while the bar is easing back out (--ac-t is 400ms)
    function isCompact() { return html.classList.contains(CLASS); }
    function measureRest() {
      if (isCompact() || Date.now() < holdUntil) return;
      var h = Math.round(header.offsetHeight);
      if (h > 0) body.style.setProperty('--ac-header-rest', h + 'px');
    }
    function release() {
      if (!isCompact()) return;
      html.classList.remove(CLASS);
      holdUntil = Date.now() + 480;
      setTimeout(measureRest, 500);
    }
    function update() {
      ticking = false;
      var y = offset();
      if (y > ON) {
        if (!isCompact()) { measureRest(); html.classList.add(CLASS); }
      } else if (y < OFF) {
        release();
      }
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }
    // Capture catches the scroll whichever element scrolls (window or .page-wrapper).
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });

    // The rest height follows the header while it is at rest (fonts, logo image, editor changes).
    if ('ResizeObserver' in window) new ResizeObserver(function () { measureRest(); }).observe(header);
    // A resize can change the rest height while compact: let the bar out, re-measure once it has settled, re-evaluate.
    window.addEventListener('resize', function () {
      if (settle) clearTimeout(settle);
      if (isCompact()) { release(); body.style.removeProperty('--ac-header-rest'); }
      settle = setTimeout(function () { measureRest(); update(); }, 520);
    }, { passive: true });

    measureRest();
    update();
  }

  function init() {
    watchAnnouncement();
    watchHero();
    intro();
    watchCompact();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
